import { format, isValid, parse } from 'date-fns';
import { parseDateValue } from './formatDateWithLocale';

/** Mantine @mantine/dates v9 datetime string format */
export const MANTINE_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** Mantine @mantine/dates v9 date-only string format (`DateStringValue`). */
export const MANTINE_DATE_FORMAT = 'yyyy-MM-dd';

/** Jackson LocalDateTime wire format (locale-agnostic, no timezone offset). */
export const API_LOCAL_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

/** Converts a Date or API date value to Mantine date-only picker string format. */
export function toMantineDateString(
  value: Date | string | number | null | undefined,
  localeCode?: string
): string {
  const date =
    value instanceof Date && isValid(value)
      ? value
      : parseDateValue(value, localeCode);
  if (!date) {
    return '';
  }
  return format(date, MANTINE_DATE_FORMAT);
}

/** Converts a Date or API date value to Mantine DateTimePicker string format. */
export function toMantineDateTimeString(
  value: Date | string | number | null | undefined,
  localeCode?: string
): string {
  const date =
    value instanceof Date && isValid(value)
      ? value
      : parseDateValue(value, localeCode);
  if (!date) {
    return '';
  }
  return format(date, MANTINE_DATETIME_FORMAT);
}

/** Parses a Mantine date-only string (`YYYY-MM-DD`) into local 00:00:00 for API wire format. */
export function fromMantineDateString(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = parse(trimmed, MANTINE_DATE_FORMAT, new Date());
  if (isValid(parsed)) {
    return parsed;
  }

  return null;
}

/** Parses a Mantine datetime string (or ISO fallback) into a Date for API payloads. */
export function fromMantineDateTimeString(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = parse(trimmed, MANTINE_DATETIME_FORMAT, new Date());
  if (isValid(parsed)) {
    return parsed;
  }

  return parseDateValue(trimmed);
}

/** Serializes a Date to the API LocalDateTime wire format. */
export function toApiLocalDateTime(
  value: Date | null | undefined
): string | null {
  if (!value || !isValid(value)) {
    return null;
  }
  return format(value, API_LOCAL_DATETIME_FORMAT);
}
